/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { HTMLAttributes, useState, useEffect } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { Picker } from '@adobe-commerce/elsie/components';
import { events } from '@adobe-commerce/event-bus';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Scope, SearchVariables, SortOrder } from '@/plp/data/models';
import { search } from '@/plp/api';

export interface SortByProps extends HTMLAttributes<HTMLDivElement> {
  scope?: Scope;
  onSortChange?: (value: SortOrder | null) => void;
}

export const SortBy: Container<SortByProps> = ({ scope, onSortChange }) => {
  const labels = useText({
    sortLabel: 'Search.SortBy.title',
    lowToHigh: 'Search.SortBy.lowToHigh',
    highToLow: 'Search.SortBy.highToLow',
  });


  const [sortOptions, setSortOptions] = useState<any[]>([]);
  const [current, setCurrent] = useState('relevance_DESC');
  const [currentSearchVariables, setCurrentSearchVariables] = useState<SearchVariables>({});

  useEffect(() => {
    // Listen for search results
    const searchEvent = events.on('search/result', (payload) => {
      setCurrentSearchVariables(payload.request);

      // Configure sort options from metadata
      if (payload.result?.metadata?.sortableAttributes) {
        setSortOptions([...payload.result.metadata.sortableAttributes]);

        // Set current sort value from request
        if (payload.request.sort) {
          const sortValue = getSortValue(payload.request.sort);

          if (sortValue) {
            setCurrent(sortValue);
          }
        }
      }
    }, { eager: true, scope });

    return () => {
      searchEvent?.off();
    };
  }, []);

  const handleSortChange = async (value: SortOrder[]): Promise<void> => {
    onSortChange?.(value[0]!);

    search({ ...currentSearchVariables, sort: value, currentPage: 1 }, { scope });
  };

  return (
    <Picker
      required
      floatingLabel={labels.sortLabel}
      {...getSortOptions(sortOptions, labels)}
      value={current}
      handleSelect={(event) => {
        const { value } = event.target as HTMLSelectElement;
        const next = getSortSearchVariables(value);
        handleSortChange(next);
      }}
    />
  );
};

/** Utility functions */

function getSortOptions(sortOptions: any, labels: any): any {
  // if sortOption is bidirectional, then we need to add two options: lowToHigh and highToLow
  const options: any[] = [];
  
  sortOptions.forEach((option: any, index: number) => {
    if (option.bidirectional) {
      // Insert the two options at the current position
      options.splice(index + options.length, 0,
        { text: `${option.label}: ${labels.lowToHigh}`, value: `${option.attribute}_ASC` },
        { text: `${option.label}: ${labels.highToLow}`, value: `${option.attribute}_DESC` }
      );
    } else {
      // Add single option at the current position
      options.splice(index + options.length, 0,
        { text: option.label, value: `${option.attribute}_DESC` }
      );
    }
  });

  return { options };
}


function getSortSearchVariables(sortValue: string): SortOrder[] {
  const index = sortValue.indexOf('_');
  const newSort: SortOrder[] = [
    {
      attribute: sortValue.substring(0, index) as string,
      direction: sortValue.substring(index + 1) === 'ASC' ? 'ASC' : 'DESC',
    },
  ];
  return newSort;
}

function getSortValue(sort: SortOrder[]) {
  if (sort.length > 0 && sort[0]) {
    return `${sort[0].attribute}_${sort[0].direction}`;
  }
  return '';
}
